import { request } from '@/renderer/utils/api';
import { IS_SERVER } from '@/renderer/utils/ssr';

// IDs of the products shown in the services section
const NFL_PRODUCT_ID = 63;

export interface DataType {
  nflProduct?: API.MembershipProduct;
}

const baseStats = {
  win: 0,
  loss: 0,
  pending: 0,
  push: 0,
  canceled: 0,
  noAction: 0,
  risk: 0,
  units: 0,
  total: 0,
};

const url =
  '/api/v1/release/stats?page[limit]=100&data[playType][]=side&data[playType][]=total&data[playType][]=prop&data[playType][]=future&data[playType][]=not-available';

export async function data(_pageContext: Vike.PageContext): Promise<DataType> {
  try {
    const [statsResult, productsResult] = await Promise.all([
      request<API.ReleaseStats>(
        {
          input: url,
          init:
            typeof AbortSignal === 'undefined'
              ? {}
              : { signal: AbortSignal.timeout(IS_SERVER ? 750 : 7500) },
        },
        { skipRefresh: true, cache: { maxAge: 5 * 60 * 1000 } },
      ),
      request<API.MembershipProduct[]>(
        {
          input: `/api/v1/product?data[ids][]=${NFL_PRODUCT_ID}`,
          init:
            typeof AbortSignal === 'undefined'
              ? {}
              : { signal: AbortSignal.timeout(IS_SERVER ? 750 : 7500) },
        },
        { skipRefresh: true, cache: { maxAge: 5 * 60 * 1000 } },
      ).catch(() => null),
    ]);

    const stats: Record<
      API.Id,
      {
        cumulative: API.ReleaseStats['seasonId'][1];
        seasons: API.ReleaseStats['seasonId'];
        firstSeasonId: API.Id;
        lastSeasonId: API.Id;
        firstSeasonDate: number;
        lastSeasonDate: number;
        last4Ids: API.Id[];
        last4: API.ReleaseStats['seasonId'][1][];
        seasonsMeta: Record<API.Id, Partial<API.Season>>;
        sportMeta: Partial<API.Sport> | undefined;
      }
    > = {};

    if (statsResult?.seasonId) {
      for (const seasonKey in statsResult.seasonId) {
        const season = statsResult.seasonId[seasonKey];
        let sportId = season.meta.sport?.[0]?.id;
        let seasonId = season.meta.season?.[0]?.id;
        const seasonStats = season.stats;
        let sportMeta: Partial<API.Sport> | undefined = {};

        if (season.meta.sport) {
          sportMeta = Object.values(season.meta.sport)[0];
          sportId = sportMeta?.id;
        }

        if (sportId !== 1 && sportId !== 2 && sportId !== 3) {
          continue; // we only care about CFB, CBB, NFL
        }

        if (!sportId || sportId < 1) {
          continue; // skip these ones
        }

        // Initialize result set by sportId
        if (!stats[sportId]) {
          stats[sportId] = {
            cumulative: { ...baseStats },
            seasons: {},
            firstSeasonId: Infinity,
            lastSeasonId: -Infinity,
            firstSeasonDate: Infinity,
            lastSeasonDate: -Infinity,
            last4Ids: [],
            last4: [],
            seasonsMeta: {},
            sportMeta,
          };
        }

        const sportRef = stats[sportId];

        let seasonDate = 0;

        if (season.meta.season) {
          const seasonMeta = Object.values(season.meta.season)[0];

          if (seasonMeta) {
            seasonId = seasonMeta.id;

            if (!seasonId || seasonId < 1) {
              continue; // skip these ones
            }

            seasonDate = Date.parse(seasonMeta.startDate || seasonMeta.endDate);

            sportRef.seasonsMeta[seasonId] = seasonMeta;
          }
        }

        if (!seasonId || seasonId < 1) {
          continue; // skip these ones
        }

        // Initialize result set season stats inside sportId
        if (!sportRef.seasons[seasonId]) {
          sportRef.seasons[seasonId] = { ...baseStats };
        }

        const seasonRef = sportRef.seasons[seasonId];

        let islast4 = false;

        // is this one of the last 4 seasons?
        if (sportRef.last4Ids.length < 4) {
          sportRef.last4Ids.push(seasonId);

          sportRef.last4Ids.sort((aSeasonId, bSeasonId) => {
            const aSeasonDate = Date.parse(
              sportRef.seasonsMeta[aSeasonId].startDate || sportRef.seasonsMeta[aSeasonId].endDate,
            );
            const bSeasonDate = Date.parse(
              sportRef.seasonsMeta[bSeasonId].startDate || sportRef.seasonsMeta[bSeasonId].endDate,
            );
            return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
          });

          sportRef.last4.push({ ...baseStats });

          islast4 = true;
        } else if (sportRef.last4Ids[0] < seasonId) {
          sportRef.last4Ids.shift();
          sportRef.last4Ids.push(seasonId);

          sportRef.last4.shift();

          sportRef.last4.push({ ...baseStats });

          const joinedSeasons = sportRef.last4Ids
            .map((seasonId, idx) => ({ seasonId: seasonId, values: sportRef.last4[idx] }))
            .sort(({ seasonId: aSeasonId }, { seasonId: bSeasonId }) => {
              const aSeasonDate = Date.parse(
                sportRef.seasonsMeta[aSeasonId].startDate ||
                  sportRef.seasonsMeta[aSeasonId].endDate,
              );
              const bSeasonDate = Date.parse(
                sportRef.seasonsMeta[bSeasonId].startDate ||
                  sportRef.seasonsMeta[bSeasonId].endDate,
              );
              return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
            });

          sportRef.last4Ids = joinedSeasons.map((item) => item.seasonId);
          sportRef.last4 = joinedSeasons.map((item) => item.values);

          islast4 = true;
        }

        // Is this the first or last season?
        if (seasonDate > sportRef.lastSeasonDate) {
          sportRef.lastSeasonDate = seasonDate;
          sportRef.lastSeasonId = seasonId;
        }

        if (seasonDate < sportRef.firstSeasonDate) {
          sportRef.firstSeasonDate = seasonDate;
          sportRef.firstSeasonId = seasonId;
        }

        for (const result in seasonStats) {
          switch (result) {
            case 'win':
            case 'loss':
            case 'pending':
            case 'push':
            case 'noAction':
            case 'canceled': {
              const resultStats = seasonStats[result];

              seasonRef[result] += resultStats.total;
              sportRef.cumulative[result] += resultStats.total;

              seasonRef.risk += season.risk;
              seasonRef.units += season.units;
              seasonRef.total += season.total;

              if (islast4) {
                sportRef.last4[sportRef.last4.length - 1][result] += resultStats.total;

                sportRef.last4[sportRef.last4.length - 1].risk += resultStats.risk;
                sportRef.last4[sportRef.last4.length - 1].units += resultStats.units;
                sportRef.last4[sportRef.last4.length - 1].total += resultStats.total;
              }
            }
          }
        }

        sportRef.cumulative.risk += season.risk;
        sportRef.cumulative.units += season.units;
        sportRef.cumulative.total += season.total;
      }
    }

    const blocks: Record<
      API.Id,
      [
        [[API.Id], API.ReleaseStats['seasonId'][1], string],
        [API.Id[], Record<keyof API.ReleaseStats['seasonId'][2], number>, string],
      ]
    > = {};

    for (const sportId of [1, 2, 3]) {
      const sportStats = stats?.[sportId];

      const currentSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 1];
      const currentSeason = sportStats?.last4[sportStats?.last4.length - 1];
      // const lastSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 2];
      // const lastSeason = sportStats?.last4[sportStats?.last4.length - 2];

      const sportPrev3Ids = sportStats?.last4Ids ? sportStats.last4Ids.slice(0, 3) : [];
      const sportPrev3 = sportStats?.last4 ? sportStats.last4.slice(0, 3) : [];

      if (sportPrev3Ids.length === sportStats?.last4Ids.length) {
        // not enough seasons, remove one more
        sportPrev3Ids.length -= 1;
        sportPrev3.length -= 1;
      }

      const sportLast3Cumulative = sportPrev3?.reduce(
        (obj, season) => {
          for (const kresult in season) {
            const result = kresult as keyof (typeof sportPrev3)[2];
            if (obj[result]) {
              obj[result] += season[result] as number;
            } else {
              obj[result] = season[result] as number;
            }
          }

          return obj;
        },
        {} as Record<keyof (typeof sportPrev3)[2], number>,
      );

      blocks[sportId] = [
        // [[currentSeasonId], currentSeason, 'current season'],
        // [[lastSeasonId], lastSeason, 'last season'],
        // [sportPrev3Ids, sportLast3Cumulative, `last ${sportPrev3Ids.length} seasons`],
        [
          [sportStats?.firstSeasonId, sportStats?.lastSeasonId],
          sportStats?.cumulative,
          '(all time)',
        ],
      ];
    }

    const nflProduct = Array.isArray(productsResult)
      ? productsResult.find((p) => p.id === NFL_PRODUCT_ID)
      : undefined;

    return { stats, sports: blocks, nflProduct };
  } catch (error) {
    console.error(error);
  }

  return {};
}
